import { flatten, uniqBy } from "lodash-es";

import { buildSavedParentMessage } from "@/app-utils/alert.js";
import { createErrorHandler } from "@/app-utils/errors.js";
import type { ISelectOption } from "@/app-utils/form.js";
import { stringify } from "@/app-utils/graphql.ts";
import type {
  ICombinedRegistrationNgsPlaybook,
  IConfiguredAttribute,
  IConfiguredPlaybook,
} from "@/app-utils/playbook.js";
import { fleshOutConfiguredAttributes, isNgsDataFileAttribute, isNgsPlaybook } from "@/app-utils/playbook.js";
import { extractSamplesEditingInput, getAttributeEncodedValue, getUniqueAttributeId } from "@/app-utils/sample.js";
import { extractNumberFromString } from "@/app-utils/string.js";
import type { Dictionary } from "@/app-utils/type.js";
import type {
  Attribute,
  EventResource,
  ExperimentInput,
  InputEditSamplesInput,
  InputExperimentInput,
  InputParentInput,
  InputPlaybookEventInput,
  InputWorkflowParamInput,
  NfSampleAttribute,
  Organism,
  Parent,
  Playbook,
  PlaybookEvent,
  PlaybookGenomeInput,
  Project,
  ProjectFile,
  Region,
  Workflow,
  WorkflowParam,
} from "@/graphql.js";
import { SelMessage } from "@/messages/SelMessage.js";
import { selMessage } from "@/messages/message.js";
import { getGroupedNearPathsBeforeLastUnderscore, isFileAccessible } from "@/modules/files/utils/file.js";
import { notify } from "@/modules/notifications/notify.js";
import {
  ATTRIBUTE_ID,
  ATTRIBUTE_TYPE,
  CELL_TYPE,
  ENTITY_TYPE,
  LOG_ACTION,
  PLAYBOOK_CLASS,
  PLAYBOOK_TYPE,
  SAMPLE_TRACKING,
  WORKFLOW_FIELD_TYPE_OPTIONS,
  WORKFLOW_PARAM_USAGE,
} from "@/settings/settings.js";
import type WebApiClient from "../../api/WebApiClient.js";
import {
  EXECUTE_ANALYSIS_REQUEST,
  EXECUTE_ANALYSIS_SUCCESS,
  LOAD_PLAYBOOK_FAILURE,
  LOAD_PLAYBOOK_REQUEST,
  LOAD_PLAYBOOK_SUCCESS,
  LOAD_WORKFLOW_FAILURE,
  LOAD_WORKFLOW_SUCCESS,
  OPEN_PLAYBOOK,
  RESET_PLAYBOOK_EXECUTION_STATE,
  RESET_SAMPLE_GRID,
  SET_USE_DEMO_DATA,
  UPDATE_PLAYBOOK_EXECUTION_STATE,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import type { IAuditLogPayload } from "./auth.js";
import type { IEditable } from "./data-editing.js";
import type { IGenomeBrowserState } from "./genome-browser.js";
import { loadApprovedWorkflows } from "./listing.js";
import { type INavbarState, showNavbarElement } from "./navbar.js";
import { fetchEventDataForRerun, openNFPlaybookForExecution } from "./nf-playbook-execution.js";
import { buildConfiguredAttribute, buildConfiguredNfAttribute } from "./playbook-admin.js";
import type {
  ExperimentLevelAttributeValueType,
  IAttributeLookupState,
  ISampleEditingState,
} from "./sample-editing.js";
import { getSampleRegistrationInput, initParentFromDemoData } from "./sample-editing.js";
import { type ITableDataList, triggerTableRefresh } from "./table-data.js";

/**
 * State definition
 */
export interface IPlaybookExecutionState {
  selectedPlaybookId: number | null; // playbook to open for execution
  playbook: IConfiguredPlaybook | null; //  full version for execution or display/editing
  workflow: Workflow | null; // workflow associated to the executed playbook
  initialPlaybookEventId: number | null;
  workflowAttributes: Attribute[]; // workflow fields turned into artificial attributes

  // execution payload
  project: Project | null;
  sampleTracking: SAMPLE_TRACKING | null;
  workflowValues: Dictionary<ExperimentLevelAttributeValueType>;
  ngsRawDataFiles: ProjectFile[][]; // TODO allow storing this in workflowValues
  eventName: string | null;
  eventDescription: string | null;

  useDemoData: boolean;
  startedFromSelectedSamples: boolean;
  contentUnavailableWarning: string | null;
}

const initialState: IPlaybookExecutionState = {
  selectedPlaybookId: null,
  playbook: null,
  workflow: null,
  initialPlaybookEventId: null,
  workflowAttributes: [],
  project: null,
  sampleTracking: null,
  workflowValues: {},
  ngsRawDataFiles: [],
  eventName: null,
  eventDescription: null,
  useDemoData: false,
  startedFromSelectedSamples: false,
  contentUnavailableWarning: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [OPEN_PLAYBOOK]: (
    state: IPlaybookExecutionState,
    payload: { playbookId: number; fromSelectedSamples: boolean },
  ): IPlaybookExecutionState => {
    return {
      ...state,
      selectedPlaybookId: payload.playbookId,
      startedFromSelectedSamples: payload.fromSelectedSamples,
    };
  },

  [LOAD_PLAYBOOK_REQUEST]: (state: IPlaybookExecutionState): IPlaybookExecutionState => {
    return { ...state, playbook: null };
  },
  [LOAD_PLAYBOOK_SUCCESS]: (state: IPlaybookExecutionState, payload: IConfiguredPlaybook): IPlaybookExecutionState => {
    return { ...state, playbook: payload };
  },
  [LOAD_PLAYBOOK_FAILURE]: (state: IPlaybookExecutionState): IPlaybookExecutionState => {
    return { ...state, playbook: null };
  },

  [LOAD_WORKFLOW_SUCCESS]: (
    state: IPlaybookExecutionState,
    payload: {
      workflow: Workflow;
      workflowAttributes: Attribute[];
    },
  ): IPlaybookExecutionState => {
    return { ...state, workflow: payload.workflow, workflowAttributes: payload.workflowAttributes };
  },
  [LOAD_WORKFLOW_FAILURE]: (state: IPlaybookExecutionState): IPlaybookExecutionState => {
    return { ...state, workflow: null };
  },

  [UPDATE_PLAYBOOK_EXECUTION_STATE]: (
    state: IPlaybookExecutionState,
    payload: Partial<IPlaybookExecutionState>,
  ): IPlaybookExecutionState => {
    return {
      ...state,
      ...payload,
      workflowValues: { ...state.workflowValues, ...payload.workflowValues }, // e.g. merge default values with demo values
      ngsRawDataFiles: payload.ngsRawDataFiles?.length ? payload.ngsRawDataFiles : state.ngsRawDataFiles,
    };
  },

  [RESET_PLAYBOOK_EXECUTION_STATE]: (): IPlaybookExecutionState => {
    return { ...initialState };
  },
  [SET_USE_DEMO_DATA]: (state: IPlaybookExecutionState): IPlaybookExecutionState => {
    return { ...state, useDemoData: true };
  },
});

/**
 * Action creators
 */
export function clearPlaybookExecutionData() {
  return {
    type: RESET_PLAYBOOK_EXECUTION_STATE,
  };
}

export function updatePlaybookExecutionState(state: Partial<IPlaybookExecutionState>) {
  return {
    type: UPDATE_PLAYBOOK_EXECUTION_STATE,
    payload: state,
  };
}

export function selectPlaybook(playbookId: number, fromSelectedSamples?: boolean) {
  return {
    type: OPEN_PLAYBOOK,
    payload: { playbookId, fromSelectedSamples },
  };
}

export function auditPlaybookOpen(playbook: Playbook) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const logMessage: string = 'Open playbook "' + playbook.name + '"';
    const payload = null;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.PLAYBOOK,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

export function auditPlaybookSubmit(playbook: Playbook) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    const logMessage: string = 'Submit playbook "' + playbook.name + '"';
    const payload = null;

    const log: IAuditLogPayload = {
      type: LOG_ACTION.PLAYBOOK,
      message: logMessage,
      payload: payload,
    };
    webApiClient.auditLog(log);
  };
}

export function loadPlaybook(playbookId: number) {
  return (
    dispatch,
    _getState: () => IAppState,
    webApiClient: WebApiClient,
  ): Promise<ICombinedRegistrationNgsPlaybook> => {
    dispatch({ type: LOAD_PLAYBOOK_REQUEST });

    return webApiClient
      .fetchPlaybook(playbookId)
      .then((playbook: Playbook) => {
        dispatch(auditPlaybookOpen(playbook));

        dispatch({
          type: LOAD_PLAYBOOK_SUCCESS,
          payload: playbook,
        });

        return playbook;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PLAYBOOK_FAILURE,
          alertTitle: `Loading of Playbook ${playbookId} Failed`,
        }),
      );
  };
}

export function loadWorkflow(playbook: ICombinedRegistrationNgsPlaybook) {
  return (dispatch, _getState, _webApiClient: WebApiClient): Promise<Workflow> => {
    const workflowID: string = playbook.config.ngsWorkflow.workflowId;
    const playbookTypeId: number = playbook.type.id;

    return dispatch(loadApprovedWorkflows(workflowID, playbookTypeId))
      .then((approvedWorkflows: Workflow[]) => {
        const selectedWorkflow: Workflow = approvedWorkflows.find((workflow) => workflow.sourceId === workflowID);

        // if workflow is not found then this error will be handled in the screen component
        if (selectedWorkflow) {
          const workflowAttributes: Attribute[] = playbook.config.experimentInput
            .filter((param: ExperimentInput) => !!param.workflowParamId)
            .map((param: ExperimentInput) =>
              selectedWorkflow.workflowParams.find((wp: WorkflowParam) => wp.id === param.workflowParamId),
            )
            .filter((workflowParam: WorkflowParam) => !!workflowParam) // make sure the workflow param still exists
            // sort by activityIndex (natural ordering of the workflow) ; should be taken care of by configuration step, but we do it again here for existing configurations
            .sort(
              (workflowParam1, workflowParam2) =>
                extractNumberFromString(workflowParam1.id) - extractNumberFromString(workflowParam2.id),
            )
            .map(convertWorkflowParamToTrackerAttribute);

          dispatch({
            type: LOAD_WORKFLOW_SUCCESS,
            payload: {
              workflow: selectedWorkflow,
              workflowAttributes: workflowAttributes,
            },
          });
        }

        return selectedWorkflow;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_WORKFLOW_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function fetchOrganismsFromSampleNgsFiles() {
  return (_dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Organism[]> => {
    const samplesState: IAttributeLookupState = getState().sampleEditingState;

    const ngsFileAttribute = getNGSFileAttributeFromWorkflowImportMapping(
      getState().playbookExecution.playbook,
      samplesState.sampleAttributes,
    );
    if (!ngsFileAttribute) {
      return Promise.resolve([]);
    }

    const ngsFiles: ProjectFile[] = [];

    // collect of ngs files inferred from the sample attribute values
    samplesState.editingGrid.forEach((row) => {
      const files: ProjectFile[] = row[getUniqueAttributeId(ngsFileAttribute)]?.value as ProjectFile[];
      if (files?.length) {
        ngsFiles.push(...files);
      }
    });

    const fileIds: number[] = ngsFiles.map((file) => file.id);

    if (!fileIds?.length) {
      return Promise.resolve([]);
    }
    // fetch the corresponding files with the organisms attachments
    return webApiClient.getOrganismsFromProjectFileById(fileIds).then((files: ProjectFile[]) => {
      return flatten(files.map((file) => file.organisms));
    });
  };
}

export function initPlaybookExecutionState() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<void> => {
    const initialPlaybookEventId = getState().playbookExecution.initialPlaybookEventId;
    const workflow = getState().playbookExecution.workflow;
    const genomeBrowser = getState().genomeBrowser;

    if (initialPlaybookEventId) {
      return dispatch(fetchEventDataForRerun(initialPlaybookEventId)).then((playbookEvent) => {
        const workflowAttributes = getState().playbookExecution.workflowAttributes;
        const playbookExecutionState: Partial<IPlaybookExecutionState> = initPlaybookExecutionFormStateFromEvent(
          playbookEvent,
          workflow?.workflowParams || [],
          workflowAttributes,
        );
        dispatch(updatePlaybookExecutionState(playbookExecutionState));
      });
    } else if (workflow) {
      let playbookExecutionState: Partial<IPlaybookExecutionState> =
        initPlaybookExecutionFormStateFromDefaultValues(workflow);
      if (genomeBrowser.contig) {
        // init from genome browser e.g. for range analysis
        playbookExecutionState = {
          ...playbookExecutionState,
          workflowValues: {
            ...playbookExecutionState.workflowValues,
            ...initWorkflowValuesFromGenomeBrowserState(workflow, genomeBrowser),
          },
        };
      }
      dispatch(updatePlaybookExecutionState(playbookExecutionState));
      return Promise.resolve();
    }
  };
}

export function initWorkflowValuesFromGenomeBrowserState(
  workflow: Workflow,
  genomeBrowser: IGenomeBrowserState,
): Dictionary<ExperimentLevelAttributeValueType> {
  const initialWorkflowValues: Dictionary<ExperimentLevelAttributeValueType> = {};
  workflow.workflowParams.forEach((workflowParam: WorkflowParam) => {
    if (workflowParam.valueTypeId === ATTRIBUTE_TYPE.GENOME_SELECTION) {
      initialWorkflowValues[workflowParam.id] = genomeBrowser.genome;
    }
    if (workflowParam.valueTypeId === ATTRIBUTE_TYPE.GENOMIC_COORDINATES) {
      const region: Partial<Region> = genomeBrowser.contig && {
        contig: { ...genomeBrowser.contig, organism: genomeBrowser.genome },
        range: genomeBrowser.selectedRange || genomeBrowser.range,
      };
      initialWorkflowValues[workflowParam.id] = region;
    }
  });
  return initialWorkflowValues;
}

export function initPlaybookExecutionStateFromDemoData() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): void => {
    // keep in mind that the demo data button was clicked (e.g. to trigger correct sample init in next step)
    dispatch({
      type: SET_USE_DEMO_DATA,
    });

    dispatch({
      type: RESET_SAMPLE_GRID,
    });

    const playbookType = getState().playbookExecution.playbook.type.id;
    const workflow = getState().playbookExecution.workflow;
    if (workflow) {
      // OK, or exclude e.g. range analysis playbooks?
      const playbookExecutionState: Partial<IPlaybookExecutionState> = initPlaybookExecutionFormStateFromDemoData(
        workflow,
        playbookType,
      );
      dispatch(updatePlaybookExecutionState(playbookExecutionState));
    }

    // initialize the parent attribute values from the demo data
    if (
      playbookType === PLAYBOOK_TYPE.SAMPLE_REGISTRATION ||
      playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES
    ) {
      const demoParent: Parent = getState().playbookExecution.playbook.templateEvent?.primaryParent;
      const parentAttributes = getState().sampleEditingState.parentType.parentAttributes;
      demoParent && dispatch(initParentFromDemoData(parentAttributes, demoParent));
    }
  };
}

export function refreshEventsListing() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const tables: ITableDataList = getState().tableData.tables;
    // first find any table that is about playbook events (can be different tableQuerySpec e.g. global events or project based events)
    const playbookEventTableKey = Object.keys(tables).find(
      (key) => tables[key].tableQuerySpec?.resultType === ENTITY_TYPE.EVENT,
    );
    if (playbookEventTableKey) {
      const playbookEventTableQuerySpec = tables[playbookEventTableKey].tableQuerySpec;
      dispatch(triggerTableRefresh(playbookEventTableQuerySpec));
    }
  };
}

export function submitPlaybook() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();
    const sampleEditingState: ISampleEditingState = getState().sampleEditingState;
    const playbookExecution: IPlaybookExecutionState = state.playbookExecution;
    const playbookType: number = playbookExecution.playbook.type.id;

    const isSampleAnalysis: boolean =
      playbookType !== PLAYBOOK_TYPE.NGS_ANALYSIS && playbookType !== PLAYBOOK_TYPE.RANGE_ANALYSIS;

    let sampleRegistrationInput: InputParentInput, editingInput: InputEditSamplesInput;
    if (isSampleAnalysis) {
      if (
        playbookExecution.playbook.type.id === PLAYBOOK_TYPE.SAMPLE_REGISTRATION ||
        playbookExecution.sampleTracking === SAMPLE_TRACKING.REGISTER
      ) {
        sampleRegistrationInput = getSampleRegistrationInput(sampleEditingState);
      } else {
        editingInput = extractSamplesEditingInput(sampleEditingState);
      }
    }

    const workflowParamInputValues: InputWorkflowParamInput[] = getWorkflowAnalysisInput(
      sampleEditingState.editingGrid,
      sampleEditingState,
      playbookExecution,
    );

    const eventInputData: InputPlaybookEventInput = {
      alias: playbookExecution.eventName,
      note: playbookExecution.eventDescription,
      playbookId: playbookExecution.playbook.id,
      projectId: playbookExecution.project.id,
    };

    dispatch({
      type: EXECUTE_ANALYSIS_REQUEST,
    });
    dispatch(auditPlaybookSubmit(playbookExecution.playbook as unknown as Playbook));

    return webApiClient
      .runPlaybook(sampleRegistrationInput, eventInputData, workflowParamInputValues, editingInput)
      .then((primaryParent: Parent) => {
        dispatch({
          type: EXECUTE_ANALYSIS_SUCCESS,
          payload: { primaryParent },
        });

        notify.info(
          {
            title: selMessage({
              id: "playbook.submission.successTitle",
              values: { isSampleAnalysis: isSampleAnalysis },
            }),
            children: isSampleAnalysis
              ? buildSavedParentMessage(primaryParent)
              : SelMessage({
                  id: "playbook.submission.successMessage",
                  values: {
                    playbookName: playbookExecution.playbook.name,
                  },
                }),
          },
          { autoClose: 10000 },
        );

        dispatch(refreshEventsListing());
      });
  };
}

export function openPlaybook(playbook: Playbook, fromSelectedSamples?: boolean) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient) => {
    if (
      playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS ||
      playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES ||
      playbook.type.id === PLAYBOOK_TYPE.PROCESSOR ||
      playbook.type.class === PLAYBOOK_CLASS.REPORT ||
      playbook.type.class === PLAYBOOK_CLASS.ALIGNMENT
    ) {
      dispatch(openNFPlaybookForExecution(playbook.id));
      return;
    } else {
      const navbarUpdateState: INavbarState = getNavbarUpdateState(playbook.type?.class);
      dispatch(selectPlaybook(playbook.id, fromSelectedSamples));
      dispatch(showNavbarElement(navbarUpdateState));
    }
  };
}

export const getNavbarUpdateState = (playbookClass?: string): INavbarState => {
  let updateState: INavbarState = {};

  switch (playbookClass) {
    case PLAYBOOK_CLASS.SAMPLE:
    case PLAYBOOK_CLASS.NGS:
    case PLAYBOOK_CLASS.RANGE:
      updateState = { showCombinedRegistrationNgsWizard: true };
      break;
    case PLAYBOOK_CLASS.BLAST:
      updateState = { showBlastPlaybookWizard: true };
      break;
  }

  return updateState;
};

/********************** Utils ***************************/

export const getNGSFileAttributeFromWorkflowImportMapping = (
  playbook: IConfiguredPlaybook,
  sampleAttributes: Attribute[],
): Attribute => {
  // processor-based ngs playbook
  if (isNgsPlaybook(playbook)) {
    let ngsDataAttribute: Attribute = null;
    playbook.config?.ngsWorkflow?.workflowImportMappings?.forEach((mapping) => {
      const attribute = sampleAttributes.find((sampleAttribute) => sampleAttribute.id === mapping.attributeId);
      if (isNgsDataFileAttribute(attribute)) {
        ngsDataAttribute = attribute;
      }
    });
    return ngsDataAttribute;
  }
  return null;
};

// init the playbook state from default values hosted in the workflow, EXCEPT for the file based values
// which are considered DEMO data
export function initPlaybookExecutionFormStateFromDefaultValues(workflow: Workflow): Partial<IPlaybookExecutionState> {
  // figure out the experiment level attributes and the ngs raw data files entries
  const experimentValues: Dictionary<ExperimentLevelAttributeValueType> = {};

  for (const workflowParam of workflow.workflowParams) {
    const experimentInputId: string | number = workflowParam.id;
    let experimentInputValue: ExperimentLevelAttributeValueType = null;

    if (workflowParam.valueTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT) {
      const defaultSelectedIndex = workflowParam.defaultParams?.selectedIndex;
      experimentInputValue =
        defaultSelectedIndex != null && // 0 should remain a valid value!
        ({
          label: workflowParam.optionLabels[defaultSelectedIndex],
          value: workflowParam.optionKeys[defaultSelectedIndex],
        } as ISelectOption);
    } else if (workflowParam.valueTypeId === ATTRIBUTE_TYPE.BOOLEAN) {
      experimentInputValue = !!workflowParam.defaultParams?.values?.[0].match(/true/);
    } else if (
      // these types are all considered DEMO data
      workflowParam.valueTypeId !== ATTRIBUTE_TYPE.FILE_OR_FOLDER &&
      workflowParam.valueTypeId !== ATTRIBUTE_TYPE.FILE &&
      workflowParam.valueTypeId !== ATTRIBUTE_TYPE.FOLDER &&
      workflowParam.valueTypeId !== ATTRIBUTE_TYPE.GENOME_SELECTION &&
      workflowParam.valueTypeId !== ATTRIBUTE_TYPE.GENOMIC_COORDINATES
    ) {
      // other simple types
      experimentInputValue = workflowParam.defaultParams?.values?.[0]; // in which case do we support multi values ?? according to ExperimentLevelAttributeValueType we do not yet
    }

    // build the initial entry
    if (experimentInputValue) {
      experimentValues[experimentInputId] = experimentInputValue;
    }
  }

  const playbookExecutionState: Partial<IPlaybookExecutionState> = {
    workflowValues: experimentValues,
    // ngs data files are considered demo data, not default values
    // sample attribute values have currently no default values
  };
  return playbookExecutionState;
}

export function initPlaybookExecutionFormStateFromDemoData(
  workflow: Workflow,
  playbookType: number,
): Partial<IPlaybookExecutionState> {
  // figure out the experiment level attributes and the ngs raw data files entries
  const experimentValues: Dictionary<ExperimentLevelAttributeValueType> = {};
  let contentUnavailableWarning: string = null;
  let ngsRawDataFiles: ProjectFile[][] = null;

  workflow?.workflowParams.forEach((workflowParam: WorkflowParam) => {
    if (isNgsDataFileWorkflowParam(workflowParam)) {
      // we have no other means for finding files than comparing the file and value list lengths (every value should have its file equivalent)
      // because the file list is always populated (even if some of the files are not accessible).
      if (workflowParam.defaultParams?.files.filter((file) => !isFileAccessible(file)).length) {
        contentUnavailableWarning = selMessage({
          id: "playbook.execution.warning.demo.fileResourcesUnavailable",
        });
      }
      // take care to group files together, since this information is not stored as part of event data
      ngsRawDataFiles = getGroupedNearPathsBeforeLastUnderscore(
        uniqBy(workflowParam.defaultParams?.files.filter(isFileAccessible), (file: ProjectFile) => file.fullPath),
      );
    } else {
      const experimentInputId: string | number = workflowParam.id;
      let experimentInputValue: ExperimentLevelAttributeValueType = null;

      if (workflowParam.valueTypeId === ATTRIBUTE_TYPE.GENOME_SELECTION) {
        if (workflowParam.defaultParams?.organism) {
          if (!workflowParam.defaultParams?.organism.hidden) {
            experimentInputValue = workflowParam.defaultParams?.organism;
          } else {
            contentUnavailableWarning = selMessage({
              id: "playbook.execution.warning.demo.genomeResourcesUnavailable",
            });
          }
        } else if (workflowParam.defaultParams?.files?.length) {
          // not sure if this check will work if a single file is not available because of the check above
          if (workflowParam.defaultParams?.files.filter((f) => !isFileAccessible(f)).length) {
            contentUnavailableWarning = selMessage({
              id: "playbook.execution.warning.demo.genomeResourcesUnavailable",
            });
          }
          experimentInputValue = workflowParam.defaultParams?.files.filter(isFileAccessible);
        }
      } else if (
        workflowParam.valueTypeId === ATTRIBUTE_TYPE.FILE_OR_FOLDER ||
        workflowParam.valueTypeId === ATTRIBUTE_TYPE.FILE ||
        workflowParam.valueTypeId === ATTRIBUTE_TYPE.FOLDER
      ) {
        if (
          workflowParam.defaultParams?.files.filter((f) => !isFileAccessible(f)).length &&
          // If not specified, the annotation of the genome is used. We must not set a warning if the annotation is missing.
          workflowParam.usage !== WORKFLOW_PARAM_USAGE.GENOMIC_ANNOTATION_INPUT
        ) {
          contentUnavailableWarning = selMessage({
            id: "playbook.execution.warning.demo.fileResourcesUnavailable",
          });
        }
        experimentInputValue = workflowParam.defaultParams?.files.filter(isFileAccessible);
      }
      // all the rest of attribute types are considered default values rather than DEMO values (already initialized)

      // build the initial entry
      if (experimentInputValue) {
        experimentValues[experimentInputId] = experimentInputValue;
      }
    }
  });

  const playbookExecutionState: Partial<IPlaybookExecutionState> = {
    workflowValues: experimentValues,
    ngsRawDataFiles: ngsRawDataFiles,
    sampleTracking:
      // sample-related standard playbooks always generate new samples
      playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES ||
      playbookType === PLAYBOOK_TYPE.SAMPLE_REGISTRATION
        ? SAMPLE_TRACKING.REGISTER
        : null,
    contentUnavailableWarning: contentUnavailableWarning,
    // sample attribute values are out of scope
  };
  return playbookExecutionState;
}

export function initPlaybookExecutionFormStateFromEvent(
  event: PlaybookEvent,
  workflowParams: WorkflowParam[],
  experimentAttributes: Attribute[],
): Partial<IPlaybookExecutionState> {
  let contentUnavailableWarning: string = null;
  // figure out the Ngs Data files (when stored outside samples attributes)
  let ngsRawDataFiles: ProjectFile[][] = null;
  const ngsDataFilesWorkflowParam = workflowParams.find(isNgsDataFileWorkflowParam);

  if (ngsDataFilesWorkflowParam) {
    // e.g. in case of sample registration there is no ngs data to initialize
    const ngsDataEventInput = event.inputs.find((input) => input.workflowField === ngsDataFilesWorkflowParam.id);
    if (ngsDataEventInput) {
      const projectFileData = getProjectFilesFromEventResourceFiles(ngsDataEventInput.resources);
      if (projectFileData.warning) {
        contentUnavailableWarning = projectFileData.warning;
      }
      // take care to group files together, since this information is not stored as part of event data
      ngsRawDataFiles = getGroupedNearPathsBeforeLastUnderscore(
        uniqBy(projectFileData.files, (file: ProjectFile) => file.fullPath),
      );
    }
  }

  // figure out the experiment level attributes corresponding to Workflow fields
  const experimentValues: Dictionary<ExperimentLevelAttributeValueType> = {};

  for (const input of event.inputs) {
    // this is the identifier of the input field, ONLY workflow field
    //const experimentInputId: string = input.workflowField;

    // the input might be a resource...
    let experimentResourceValues: ExperimentLevelAttributeValueType = null;

    if (input.resources?.length) {
      if (input.resources[0].file) {
        const projectFileData = getProjectFilesFromEventResourceFiles(input.resources);
        experimentResourceValues = projectFileData.files;

        if (projectFileData.warning) {
          contentUnavailableWarning = projectFileData.warning;
        }
      } else if (input.resources[0].organism) {
        if (!input.resources[0].organism.hidden) {
          experimentResourceValues = input.resources[0].organism;
        } else {
          contentUnavailableWarning = selMessage({
            id: "playbook.execution.warning.event.genomeResourcesUnavailable",
          });
        }
      } else if (input.resources[0].region) {
        const region: Region = input.resources[0].region; // never multiple regions
        experimentResourceValues = region;
      }

      // build the initial entry as resource
      experimentValues[input.workflowField] = experimentResourceValues;
    } else {
      // ..or a simple input (every workflow field that is not considered a resource)
      const experimentAttribute = experimentAttributes.find((attribute) => attribute.id === input.workflowField);

      if (experimentAttribute) {
        let firstInputValue: string | boolean = input.inputValues?.[0]; // in which case do we support multi values ?? according to ExperimentLevelAttributeValueType we do not yet

        // We should do it in getAttributeEncodedValue but not sure of the side effect for now
        if (experimentAttribute.attributeType.id === ATTRIBUTE_TYPE.BOOLEAN) {
          firstInputValue =
            typeof firstInputValue === "boolean"
              ? firstInputValue
              : !!firstInputValue.match(/true/) || !!firstInputValue.match(/yes/);
        }

        // build the initial entry as simple input value
        experimentValues[input.workflowField] = getAttributeEncodedValue(experimentAttribute, firstInputValue);
      }
    }
  }

  /*
  // resurrect the parent attribute values from the event: out of scope for now
  // figure out the experiment level attributes corresponding to Parent attributes
  event.primaryParent?.attributeValues.forEach((attributeValue: IAttributeValue) => {
    // figure out the the experiment level attribute from the attribute value's attribute
    const experimentAttribute = experimentAttributes.find((attribute) => attributeValue.attribute.id === attribute.id);
    if (experimentAttribute) {
      const experimentInputValue: ExperimentLevelAttributeValueType = getAttributeEncodedValue(
        experimentAttribute,
        getRawAttributeValue(attributeValue),
      );
      // build the initial entry as parent attribute value
      experimentValues[getUniqueAttributeId(experimentAttribute)] = experimentInputValue;
    }
  });
   */

  return {
    project: event.project,
    workflowValues: experimentValues,
    ngsRawDataFiles: ngsRawDataFiles,
    contentUnavailableWarning: contentUnavailableWarning,
    // sample attribute values are out of scope
  };
}

export function getProjectFilesFromEventResourceFiles(resources: EventResource[]): {
  warning: string;
  files: ProjectFile[];
} {
  const allFiles = resources.map((resource) => resource.file);
  const filesUnavailable: ProjectFile[] = allFiles.filter((f) => !isFileAccessible(f));
  return {
    warning:
      filesUnavailable.length === 0
        ? null
        : selMessage({ id: "playbook.execution.warning.event.fileResourcesUnavailable" }),
    files: allFiles.filter(isFileAccessible),
  };
}

export function extractWorkflowParamInputValues(
  editingGrid: IEditable[],
  sampleEditingState: ISampleEditingState,
  playbookExecution: IPlaybookExecutionState,
): InputWorkflowParamInput[] {
  const playbook: IConfiguredPlaybook = playbookExecution.playbook;
  const workflow: Workflow = playbookExecution.workflow;

  // get values of the experiment level workflow fields (stored as parent attributes)
  const workflowParamInputValues: InputWorkflowParamInput[] = playbookExecution.workflowAttributes.map(
    (attribute: Attribute) => {
      const value: ExperimentLevelAttributeValueType =
        playbookExecution.workflowValues[getUniqueAttributeId(attribute)];
      let workflowParamInputValue: string[] = [];
      if (value && attribute.attributeType.id === ATTRIBUTE_TYPE.GENOME_SELECTION) {
        const isFileSelection = Array.isArray(value);
        // create a DTO to communicate properly all the possible options for selecting a reference
        const genomeSelection: PlaybookGenomeInput = {
          genomeId: !isFileSelection ? (value as Organism)?.id : null,
          fileId: isFileSelection ? (value as ProjectFile[])?.[0]?.id : null,
        };
        workflowParamInputValue = [JSON.stringify(genomeSelection)]; // need a string !
      } else if (attribute.attributeType.id === ATTRIBUTE_TYPE.GENOMIC_COORDINATES) {
        const region = value as Region;
        workflowParamInputValue = region
          ? [stringify(region.contig.id), stringify(region.range.start), stringify(region.range.stop)]
          : [];
      } else if (
        attribute.attributeType.id === ATTRIBUTE_TYPE.FILE_OR_FOLDER ||
        attribute.attributeType.id === ATTRIBUTE_TYPE.FILE ||
        attribute.attributeType.id === ATTRIBUTE_TYPE.FOLDER
      ) {
        if (value) {
          workflowParamInputValue = (value as ProjectFile[]).map((file: ProjectFile) => file.fullPath);
        }
        // Special handling for the annotation attributes which are not set
        // because the default annotation gff file of the Explorer genome will be used.
        // In this case, MT expects an array with an empty string.
        if (
          attribute.usage === WORKFLOW_PARAM_USAGE.GENOMIC_ANNOTATION_INPUT &&
          attribute.mandatory &&
          workflowParamInputValue.length === 0
        ) {
          workflowParamInputValue.push("");
        }
      } else if (value && attribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT) {
        workflowParamInputValue = [(value as ISelectOption).value];
      } else if (value != null && attribute.attributeType.id === ATTRIBUTE_TYPE.BOOLEAN) {
        workflowParamInputValue = [stringify(value)];
      } else if (value != null) {
        workflowParamInputValue = [value as string];
      }

      const workflowParamInput: InputWorkflowParamInput = {
        id: attribute.id,
        values: workflowParamInputValue, // expects a list of values
        sampleName: null,
      };
      return workflowParamInput;
    },
  );

  // we construct the list of ngs files, optionally mapped to samples
  const ngsRawDataFileWorkflowParam: WorkflowParam = workflow.workflowParams.find(isNgsDataFileWorkflowParam);
  if (ngsRawDataFileWorkflowParam) {
    if (playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS) {
      playbookExecution.ngsRawDataFiles.map((files: ProjectFile[]) => {
        const ngsRawDataFilePathSetting: InputWorkflowParamInput = {
          id: ngsRawDataFileWorkflowParam.id,
          sampleName: null,
          values: files.map((file: ProjectFile) => file.fullPath), // expects a list of values
        };
        workflowParamInputValues.push(ngsRawDataFilePathSetting);
      });
    } else {
      // for every registered samples, we extract the workflow parameters
      editingGrid.map((editingRow: IEditable) => {
        const ngsRawDataFileAttribute = getNGSFileAttributeFromWorkflowImportMapping(
          playbook,
          sampleEditingState.sampleAttributes,
        );
        const sampleNameAttribute: Attribute = sampleEditingState.sampleAttributes.find(
          (attribute: Attribute) => attribute.id === ATTRIBUTE_ID.SAMPLE_NAME,
        );
        const sampleName: string = editingRow[getUniqueAttributeId(sampleNameAttribute)] as string;
        const ngsFileValues: ProjectFile[] = editingRow[getUniqueAttributeId(ngsRawDataFileAttribute)] as ProjectFile[];

        const ngsRawDataFilePathSetting: InputWorkflowParamInput = {
          id: ngsRawDataFileWorkflowParam.id,
          sampleName: sampleName,
          values: ngsFileValues?.map((file: ProjectFile) => file.fullPath) || [], // expects a list of values
        };
        workflowParamInputValues.push(ngsRawDataFilePathSetting);
      });
    }
  }

  return workflowParamInputValues;
}

function getWorkflowAnalysisInput(
  editingGrid: IEditable[],
  sampleEditingState: ISampleEditingState,
  playbookExecutionState: IPlaybookExecutionState,
): InputWorkflowParamInput[] {
  let workflowParamInputValues: InputWorkflowParamInput[] = [];
  // fill out workflow settings DTO IF APPLICABLE (NGS Analysis case)
  if (
    isNgsPlaybook(playbookExecutionState.playbook) &&
    playbookExecutionState.playbook?.config?.ngsWorkflow?.workflowId
  ) {
    workflowParamInputValues = extractWorkflowParamInputValues(editingGrid, sampleEditingState, playbookExecutionState);
  }
  return workflowParamInputValues;
}

export function convertWorkflowParamToTrackerAttribute(workflowParam: WorkflowParam): Attribute {
  let attributeType: ISelectOption = WORKFLOW_FIELD_TYPE_OPTIONS.find(
    (option: ISelectOption) => option.value === workflowParam.valueTypeId,
  );
  if (!attributeType) {
    attributeType = WORKFLOW_FIELD_TYPE_OPTIONS[0]; // just in case the type could not be determined
  }
  return {
    id: workflowParam.id,
    attributeName: workflowParam.label,
    groupName: workflowParam.activityName,
    attributeType: { id: attributeType.value, name: attributeType.label },
    usage: workflowParam.usage,
    modifiable: true,
    editable: true,
    mandatory: workflowParam.required,
    multipleValues: workflowParam.multiSelect,
    isWorkflowParam: true, // THIS IS IMPORTANT PIECE TO DISTINGUISH THE STANDARD TRACKER ATTRIBUTES FROM THE WORKFLOW PARAMS
    vocabularyTerms: workflowParam.optionLabels?.map((optionLabel: string, idx) => {
      return { attribute: null, id: workflowParam.optionKeys[idx], name: optionLabel, sortOrder: idx };
    }),
    tableColumn: {
      // important when the attribute if shown as part of the Review grid
      cellType: CELL_TYPE.STRING,
      name: workflowParam.label,
    },
  } as Partial<Attribute> as Attribute;
}

export function isNgsDataFileAttributeByUsage(attribute: Attribute) {
  return (
    attribute.usage === WORKFLOW_PARAM_USAGE.UNALIGNED_READS_INPUT_FILE_PATHS ||
    attribute.usage === WORKFLOW_PARAM_USAGE.ALIGNED_READS_INPUT_FILE_PATHS
  );
}

export function isNgsDataFileWorkflowParam(workflowParam: WorkflowParam) {
  return (
    workflowParam.usage === WORKFLOW_PARAM_USAGE.UNALIGNED_READS_INPUT_FILE_PATHS ||
    workflowParam.usage === WORKFLOW_PARAM_USAGE.ALIGNED_READS_INPUT_FILE_PATHS
  );
}

export function isNgsDataExperimentAttribute(attribute: Attribute) {
  return (
    attribute.usage === WORKFLOW_PARAM_USAGE.GENOME_INPUT ||
    attribute.usage === WORKFLOW_PARAM_USAGE.GENOMIC_ANNOTATION_INPUT
  );
}

export function getGenomeWorkflowParams(experimentAttributes: Attribute[]): Attribute[] {
  return experimentAttributes.filter(
    (attribute: Attribute) =>
      isNgsDataExperimentAttribute(attribute) && attribute.usage === WORKFLOW_PARAM_USAGE.GENOME_INPUT,
  );
}

export function getOrderedListOfTrackerAndWorkflowAttributes(
  playbookConfiguredAttributes: InputExperimentInput[], // configured as part of the playbook, sorted list
  attributesWithAllData: Attribute[],
): IConfiguredAttribute[] {
  const configuredAttributesWithBasicInformation: Array<Partial<IConfiguredAttribute>> =
    playbookConfiguredAttributes.map(buildConfiguredAttribute);

  return fleshOutConfiguredAttributes(configuredAttributesWithBasicInformation, attributesWithAllData);
}

export function getOrderedListOfNfTrackerAttributes(
  playbookConfiguredAttributes: NfSampleAttribute[], // configured as part of the playbook, sorted list
  attributesWithAllData: Attribute[],
): IConfiguredAttribute[] {
  const configuredAttributesWithBasicInformation: Array<Partial<IConfiguredAttribute>> =
    playbookConfiguredAttributes.map((a) => buildConfiguredNfAttribute(a));

  return fleshOutConfiguredAttributes(configuredAttributesWithBasicInformation, attributesWithAllData);
}
